import { useState, useEffect, useCallback } from 'react'
import { useDisclosure } from '@chakra-ui/react'
import { useAuthQueryParam } from '@/lib/hooks/useAuthQueryParam'
import { toast } from 'sonner'
import { getSharePasscode } from '@/lib/storage/local-storage'
import { getShareExtension, buildShareUrl, buildFullShareUrl } from '@/lib/utils/share-url'
import {
  listApiKeyShares,
  validateApiKey,
  revokeShareWithApiKey,
  type ShareSummary,
  type UsageInfo,
} from '@/lib/api/keys'
import {
  validateAccountName,
  validateApiKeyFormat,
  signUpAccount,
  signInWithApiKey,
  batchDecryptPasscodes,
  commitApiKey,
  getShareDisplayName,
  isShareEncrypted,
  getStoredApiKey,
} from '@/lib/account/auth'
import { useCopyToClipboard } from '@/lib/hooks/useCopyToClipboard'

interface UseSidebarAccountOptions {
  onUsageChange?: (usage: UsageInfo | null) => void
}

/**
 * Manages account state, share loading, passcode decryption, sign-up/sign-in flows,
 * and share action handlers (copy, revoke, view details).
 */
export function useSidebarAccount({ onUsageChange }: UseSidebarAccountOptions) {
  // Account state
  const [shares, setShares] = useState<ShareSummary[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [hasApiKey, setHasApiKey] = useState(false)
  const [accountName, setAccountName] = useState<string | null>(null)
  const [apiKey, setApiKey] = useState<string | null | undefined>(null)
  const [usage, setUsage] = useState<UsageInfo | null>(null)
  const [accountExpiresAt, setAccountExpiresAt] = useState<number | null>(null)
  const [decryptedPasscodes, setDecryptedPasscodes] = useState<Record<string, string>>({})
  const { copy: copyToClipboard, hasCopied: isCopied } = useCopyToClipboard()

  // Share details modal
  const {
    isOpen: isDetailsOpen,
    onOpen: onDetailsOpen,
    onClose: onDetailsClose,
  } = useDisclosure()
  const [selectedShareForDetails, setSelectedShareForDetails] =
    useState<ShareSummary | null>(null)

  // Sign up state
  const [signUpUsername, setSignUpUsername] = useState('')
  const [signUpUsernameError, setSignUpUsernameError] = useState('')
  const [isSigningUp, setIsSigningUp] = useState(false)
  const [generatedKey, setGeneratedKey] = useState<string | null>(null)
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [termsError, setTermsError] = useState(false)

  // Sign in state
  const [signInKey, setSignInKey] = useState('')
  const [signInError, setSignInError] = useState('')
  const [isSigningIn, setIsSigningIn] = useState(false)

  // Auth modals
  const {
    isOpen: isSignUpOpen,
    onOpen: onSignUpOpen,
    onClose: onSignUpClose,
  } = useDisclosure()
  const {
    isOpen: isSignInOpen,
    onOpen: onSignInOpen,
    onClose: onSignInClose,
  } = useDisclosure()

  // Handle ?auth=signup / ?auth=signin query param
  useAuthQueryParam({ hasApiKey, onSignUpOpen, onSignInOpen })

  // --- Load shares ---
  const loadShares = async () => {
    const storedKey = getStoredApiKey()
    if (!storedKey) {
      setHasApiKey(false)
      setAccountName(null)
      setApiKey(null)
      return
    }

    setHasApiKey(true)
    setApiKey(storedKey)
    setIsLoading(true)
    try {
      const [sharesData, statsData] = await Promise.all([
        listApiKeyShares(storedKey),
        validateApiKey(storedKey),
      ])
      setShares(sharesData.shares)
      if (statsData.valid) {
        if (statsData.name) {
          setAccountName(statsData.name)
        }
        if (statsData.usage) {
          setUsage(statsData.usage)
        }
        setAccountExpiresAt(statsData.expiresAt ?? null)
      }
    } catch (error) {
      console.error('Failed to load shares:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadShares()

    const handleApiKeyChange = () => {
      loadShares()
    }

    window.addEventListener('apikey-changed', handleApiKeyChange)
    window.addEventListener('share-created', handleApiKeyChange)

    return () => {
      window.removeEventListener('apikey-changed', handleApiKeyChange)
      window.removeEventListener('share-created', handleApiKeyChange)
    }
  }, [])

  // Notify parent when usage changes
  useEffect(() => {
    onUsageChange?.(usage)
  }, [usage, onUsageChange])

  // Decrypt wrapped passcodes when shares change
  useEffect(() => {
    if (!apiKey) return

    batchDecryptPasscodes(shares, apiKey).then((newPasscodes) => {
      if (Object.keys(newPasscodes).length > 0) {
        setDecryptedPasscodes((prev) => ({ ...prev, ...newPasscodes }))
      }
    })
  }, [shares, apiKey])

  // --- Passcode / URL helpers ---
  const getPasscode = useCallback(
    (shareId: string): string | null => {
      return getSharePasscode(shareId) || decryptedPasscodes[shareId] || null
    },
    [decryptedPasscodes],
  )

  const getShareUrl = (share: ShareSummary, mode?: 'edit') => {
    const ext = getShareExtension(share.filename)
    return buildShareUrl(share.id, ext, getPasscode(share.id), mode)
  }

  const getFullShareUrl = (share: ShareSummary) => {
    const ext = getShareExtension(share.filename)
    return buildFullShareUrl(share.id, ext, getPasscode(share.id))
  }

  // --- Share actions ---
  const handleCopyLink = (share: ShareSummary) => {
    copyToClipboard(getFullShareUrl(share), share.id, 'Link copied!')
  }

  const handleRevoke = async (shareId: string) => {
    if (!apiKey) return

    toast.promise(
      revokeShareWithApiKey(shareId, apiKey).then(() => {
        loadShares()
      }),
      {
        loading: 'Deleting share...',
        success: 'Share deleted',
        error: 'Failed to delete share',
      },
    )
  }

  const handleViewDetails = (share: ShareSummary) => {
    setSelectedShareForDetails(share)
    onDetailsOpen()
  }

  // --- Sign up ---
  const handleSignUp = async () => {
    const nameCheck = validateAccountName(signUpUsername)
    if (!nameCheck.valid) {
      setSignUpUsernameError(nameCheck.error)
      return
    }

    if (!acceptedTerms) {
      setTermsError(true)
      return
    }

    setSignUpUsernameError('')
    setTermsError(false)
    setIsSigningUp(true)
    try {
      const data = await signUpAccount(signUpUsername)
      setGeneratedKey(data.apiKey)
    } catch (error) {
      console.error('Failed to create account:', error)
      toast.error('Failed to create account. Please try again.')
    } finally {
      setIsSigningUp(false)
    }
  }

  const handleSignUpComplete = () => {
    if (generatedKey) {
      commitApiKey(generatedKey)
      setHasApiKey(true)
      setAccountName(signUpUsername.trim())
      toast.success(`Welcome, ${signUpUsername.trim()}!`)
      setGeneratedKey(null)
      setSignUpUsername('')
      onSignUpClose()
    }
  }

  const handleSignUpCancel = () => {
    setGeneratedKey(null)
    setSignUpUsername('')
    setSignUpUsernameError('')
    setAcceptedTerms(false)
    setTermsError(false)
    onSignUpClose()
  }

  // --- Sign in ---
  const handleSignIn = async () => {
    const formatCheck = validateApiKeyFormat(signInKey)
    if (!formatCheck.valid) {
      setSignInError(formatCheck.error)
      return
    }

    setSignInError('')
    setIsSigningIn(true)
    try {
      const result = await signInWithApiKey(signInKey)
      if (!result.valid) {
        setSignInError(result.error)
        return
      }

      commitApiKey(signInKey.trim())
      setHasApiKey(true)
      setAccountName(result.stats.name || null)
      setApiKey(signInKey.trim())
      toast.success(
        `Welcome back${result.stats.name ? `, ${result.stats.name}` : ''}!`,
      )
      setSignInKey('')
      onSignInClose()
    } catch (error) {
      console.error('Failed to sign in:', error)
      setSignInError('Failed to validate key. Please try again.')
    } finally {
      setIsSigningIn(false)
    }
  }

  const handleSignInCancel = () => {
    setSignInKey('')
    setSignInError('')
    onSignInClose()
  }

  // --- Sign out ---
  const handleSignOutComplete = () => {
    setHasApiKey(false)
    setShares([])
    setAccountName(null)
  }

  return {
    // Account state
    shares,
    isLoading,
    hasApiKey,
    apiKey,
    accountName,
    usage,
    accountExpiresAt,

    // Share actions
    loadShares,
    handleCopyLink,
    handleRevoke,
    handleViewDetails,
    isCopied,
    getPasscode,
    getShareUrl,
    getFullShareUrl,
    getDisplayName: getShareDisplayName,
    isEncrypted: isShareEncrypted,

    // Share details modal
    isDetailsOpen,
    onDetailsClose,
    selectedShareForDetails,

    // Sign up
    isSignUpOpen,
    onSignUpOpen,
    signUpUsername,
    setSignUpUsername,
    signUpUsernameError,
    setSignUpUsernameError,
    isSigningUp,
    generatedKey,
    acceptedTerms,
    setAcceptedTerms,
    termsError,
    setTermsError,
    handleSignUp,
    handleSignUpComplete,
    handleSignUpCancel,

    // Sign in
    isSignInOpen,
    onSignInOpen,
    signInKey,
    setSignInKey,
    signInError,
    setSignInError,
    isSigningIn,
    handleSignIn,
    handleSignInCancel,

    // Sign out
    handleSignOutComplete,
  }
}
